import { ContactUsFormSchema } from './contact-us-schema';
import { MDSText } from '@thrivent/midwest-web';
import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from 'react-google-recaptcha';
import {
  UseControllerProps,
  UseFormSetValue,
  useController,
} from 'react-hook-form';
import styled from 'styled-components';

// --------------------------------------------------------------------------

const ReCaptchaLabel = styled.label`
  display: none;
`;
export const ErrorText = styled(MDSText.BodyMediumSm)`
  color: ${(p) => p.theme.midwestColors.sentimentDangerStrong};
`;

// --------------------------------------------------------------------------

export const ReCaptcha = ({
  className,
  setValue,
  name,
  rules,
}: {
  className?: string;
  setValue: UseFormSetValue<ContactUsFormSchema>;
  name: string;
} & Pick<UseControllerProps, 'rules'>) => {
  const {
    field: { onChange: formOnChange },
    fieldState: { error },
    formState,
  } = useController({ name, rules });
  const invalid = !!error;
  const captcha = useRef<ReCAPTCHA | null>(null);
  const errorRef = useRef<HTMLSpanElement | null>(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClient(true);
    }
  }, [setIsClient]);

  // This useEffect is an accessibility fix.
  // We just want to make sure a failed/unfilled
  // reCAPTCHA is announced to the user on attempted form submit
  useEffect(() => {
    if (
      invalid &&
      Object.keys(formState?.errors ?? {}).length === 1 &&
      formState?.errors?.['g-recaptcha-response'] &&
      errorRef.current
    ) {
      errorRef.current.focus();
    }
  }, [invalid, formState]);

  const recaptchaKey =
    process.env['NEXT_PUBLIC_CONTACT_US_RECAPTCHA_KEY'] ??
    'NEXT_PUBLIC_CONTACT_US_RECAPTCHA_KEY_NOT_SET';
  const handleChange = (val: string | null) => {
    formOnChange(val);
    setValue('g-recaptcha-response', val ?? '');
  };

  return (
    <div className={className} data-testid="re-captcha">
      <ReCaptchaLabel htmlFor="g-recaptcha-response">
        ReCaptcha Response
      </ReCaptchaLabel>
      {isClient && (
        <ReCAPTCHA
          sitekey={recaptchaKey}
          ref={captcha}
          onChange={handleChange}
        />
      )}
      {invalid && (
        <ErrorText role="alert" tabIndex="0" ref={errorRef}>
          reCAPTCHA invalid
        </ErrorText>
      )}
    </div>
  );
};
